import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { BookOpen, Mail, Lock, Eye, EyeOff, AlertCircle, X, Check, Star } from 'lucide-react';
import { useConfirmation } from '../context/ConfirmationContext';
import { useAuth } from '../context/AuthContext';

const LoginPage = () => {
  const navigate = useNavigate();
  const { login, user } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const hasRedirected = useRef(false);
  const particlesRef = useRef(null);
  const suppressedCountRef = useRef(0);

  const [particlesEnabled, setParticlesEnabled] = useState(true);
  const { showConfirmation } = useConfirmation();
  const [bgLoaded, setBgLoaded] = useState(false);

  // Preload background image and fade it in when ready
  useEffect(() => {
    const img = new Image();
    img.src = '/images/login-bg.png';
    img.decoding = 'async';
    img.onload = () => setBgLoaded(true);
    img.onerror = () => setBgLoaded(true); // still show fallback if load fails
  }, []);

  // Generate particle CSS once and keep it stable across renders so typing doesn't reposition them
  const particleCSS = useMemo(() => {
    const lines = [];
    for (let i = 0; i < 18; i++) {
      const size = Math.round(6 + Math.random() * 24);
      const left = Math.round(Math.random() * 100);
      const top = Math.round(Math.random() * 100);
      const delay = Math.round(Math.random() * 5000);
      const dur = 6000 + Math.round(Math.random() * 8000);
      const color = ['#fff', '#c7b2ff', '#bde3ff', '#ffd1e8'][i % 4];
      lines.push(`.particle-${i} { width: ${size}px; height: ${size}px; left: ${left}%; top: ${top}%; background: ${color}; animation: float${i} ${dur}ms ease-in-out ${delay}ms infinite; }`);
      lines.push(`@keyframes float${i} { 0%{ transform: translateY(0) } 50%{ transform: translateY(-20px) } 100%{ transform: translateY(0) } }`);
    }
    return lines.join('\n');
  }, []);

  // Particle interactivity: parallax movement based on cursor over the login container
  useEffect(() => {
    if (!particlesEnabled) return;
    const container = particlesRef.current;
    if (!container) return;
    try {
      const particles = Array.from(container.querySelectorAll('.particle'));
      // Assign a random speed factor for subtle depth
      particles.forEach(p => p.dataset.speed = (0.2 + Math.random() * 1.0).toFixed(2));

      console.debug('[Login] Particles initialized:', particles.length);

      let raf = null;
      const onMove = (e) => {
        const rect = container.getBoundingClientRect();
        const x = (e.clientX - rect.left) / rect.width; // 0..1
        const y = (e.clientY - rect.top) / rect.height; // 0..1
        const cx = x - 0.5; // -0.5 .. 0.5
        const cy = y - 0.5;

        if (raf) cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => {
          particles.forEach((p, i) => {
            const speed = parseFloat(p.dataset.speed) || 0.3;
            // variation by particle index to avoid uniform movement
            const signX = (i % 2 === 0) ? 1 : -1;
            const tx = Math.round(cx * 40 * speed * signX);
            const ty = Math.round(cy * 40 * speed * (1 - (i % 3) * 0.1));
            p.style.transform = `translate3d(${tx}px, ${ty}px, 0)`;
          });
        });
      };

      const onLeave = () => {
        if (raf) cancelAnimationFrame(raf);
        particles.forEach(p => { p.style.transform = ''; });
      };

      // Repulse effect on click: push nearby particles away briefly
      const onClick = (e) => {
        const rect = container.getBoundingClientRect();
        const cx = e.clientX - rect.left;
        const cy = e.clientY - rect.top;
        const radius = 150;

        particles.forEach((p, i) => {
          const r = p.getBoundingClientRect();
          const px = r.left + r.width / 2 - rect.left;
          const py = r.top + r.height / 2 - rect.top;
          const dx = px - cx;
          const dy = py - cy;
          const dist = Math.hypot(dx, dy);
          if (dist < radius) {
            const push = ((radius - dist) / radius) * 120; // px to push
            const nx = dx / (dist || 1);
            const ny = dy / (dist || 1);
            const repX = Math.round(nx * push);
            const repY = Math.round(ny * push);
            const prev = p.style.transform || '';
            p.style.transition = 'transform 600ms cubic-bezier(0.2,0.8,0.2,1)';
            // append the repulse translation
            p.style.transform = `${prev} translate3d(${repX}px, ${repY}px, 0)`;
            setTimeout(() => {
              // revert to previous transform (parallax will update on next mouse move)
              p.style.transition = 'transform 400ms ease';
              p.style.transform = prev;
            }, 600);
          }
        });
      };

      // Subtle scroll reaction: bump particles briefly based on scroll
      let scrollRaf = null;
      const onScroll = () => {
        if (scrollRaf) cancelAnimationFrame(scrollRaf);
        scrollRaf = requestAnimationFrame(() => {
          const s = window.scrollY / 20; // small factor
          particles.forEach((p, i) => {
            const speed = parseFloat(p.dataset.speed) || 0.3;
            const extra = Math.round(s * speed * (i % 2 === 0 ? 1 : -1));
            const prev = p.style.transform || '';
            p.style.transition = 'transform 300ms ease';
            p.style.transform = `${prev} translate3d(0, ${extra}px, 0)`;
            setTimeout(() => { p.style.transform = prev; }, 350);
          });
        });
      };

      container.addEventListener('mousemove', onMove);
      container.addEventListener('mouseleave', onLeave);
      container.addEventListener('click', onClick);
      window.addEventListener('scroll', onScroll);

      return () => {
        container.removeEventListener('mousemove', onMove);
        container.removeEventListener('mouseleave', onLeave);
        container.removeEventListener('click', onClick);
        window.removeEventListener('scroll', onScroll);
        if (raf) cancelAnimationFrame(raf);
        if (scrollRaf) cancelAnimationFrame(scrollRaf);
      };
    } catch (err) {
      console.warn('[Login] Particle init failed, disabling particles:', err && err.message ? err.message : err);
      setParticlesEnabled(false);
    }
  }, [particlesEnabled]);

  useEffect(() => {
    // Suppress noisy extension errors in the console that originate from browser extensions
    const patterns = ['FrameIsBrowserFrameError', 'The message port closed', 'Could not establish connection', 'ERR_FILE_NOT_FOUND', 'background.js', 'extensionState.js', 'heuristicsRedefinitions.js', 'utils.js'];

    const onUnhandled = (e) => {
      const reason = e?.reason || '';
      const msg = (reason && typeof reason === 'object' && reason.message) ? reason.message : String(reason || e?.reason || '');
      if (patterns.some(p => msg.includes(p))) {
        e.preventDefault();
        suppressedCountRef.current++;
        // keep minimal debug so you can still notice it's being suppressed when developing
        console.debug('[Login] suppr:', msg);
      }
    };

    const onError = (e) => {
      const filename = e?.filename || '';
      const msg = e?.message || '';
      if (patterns.some(p => filename.includes(p) || msg.includes(p))) {
        e.preventDefault();
        suppressedCountRef.current++;
        console.debug('[Login] suppr err:', filename || msg);
      }
    };

    // Wrap console.error/warn to filter noise from extensions while preserving other messages
    const origError = console.error.bind(console);
    const origWarn = console.warn.bind(console);
    const stringifyArgs = (args) => args.map(a => (typeof a === 'string' ? a : (a && a.message) ? a.message : JSON.stringify(a))).join(' ');
    console.error = (...args) => {
      try {
        const s = stringifyArgs(args);
        if (patterns.some(p => s.includes(p))) { suppressedCountRef.current++; return; }
      } catch (e) { /* ignore stringify errors */ }
      origError(...args);
    };
    console.warn = (...args) => {
      try {
        const s = stringifyArgs(args);
        if (patterns.some(p => s.includes(p))) { suppressedCountRef.current++; return; }
      } catch (e) { /* ignore stringify errors */ }
      origWarn(...args);
    };

    window.addEventListener('unhandledrejection', onUnhandled);
    window.addEventListener('error', onError);

    return () => {
      window.removeEventListener('unhandledrejection', onUnhandled);
      window.removeEventListener('error', onError);
      // restore console methods
      console.error = origError;
      console.warn = origWarn;
      if (suppressedCountRef.current > 0) console.debug(`[Login] suppressed ${suppressedCountRef.current} extension messages`);
    };
  }, []);

  const redirectByRole = (roleId) => {
    if (hasRedirected.current) return; // Prevent multiple redirects
    hasRedirected.current = true;
    
    switch (roleId) {
      case 1:
        navigate('/admin', { replace: true });
        break;
      case 2:
        navigate('/lecturer', { replace: true });
        break;
      case 3:
        navigate('/student', { replace: true });
        break;
      default:
        navigate('/', { replace: true });
    }
  };

  // Load saved email on mount
  useEffect(() => {
    const savedEmail = localStorage.getItem('rememberedEmail');
    if (savedEmail) {
      setEmail(savedEmail);
      setRememberMe(true);
    }
  }, []); // ONLY run once on mount

  // Redirect when user changes (after login)
  useEffect(() => {
    if (user?.roleId) {
      redirectByRole(user.roleId);
    }
  }, [user]);

  const showError = (message) => {
    setErrorMessage(message);
  };

  const closeError = () => {
    setErrorMessage('');
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    closeError();
    setLoading(true);

    // Email validation
    if (!email.trim()) {
      showError('Please enter your email address');
      setLoading(false);
      return;
    }

    // Password validation
    if (!password.trim()) {
      showError('Please enter your password');
      setLoading(false);
      return;
    }

    // Password length validation
    if (password.length < 8) {
      showError('Password must be at least 8 characters');
      setLoading(false);
      return;
    }

    // Email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      showError('Please enter a valid email address');
      setLoading(false);
      return;
    }

    try {
      // API login call
      const result = await login(email, password);
      
      if (!result.success) {
        const errorMsg = result.message || 'Invalid email or password';
        // If account is suspended (HTTP 403) or message mentions suspension, show a professional confirmation modal with contact option
        if (result.status === 403 || /suspend|suspended|deactivate|deactivated/i.test(errorMsg)) {
          showConfirmation({
            title: 'Account suspended',
            message: `Your account has been suspended by the school administrator. Please contact the administrator to restore access.\n\nYou can reach the administrator via WhatsApp at +94 77 194 5267.`,
            confirmText: 'Contact via WhatsApp',
            cancelText: 'Close',
            type: 'warning',
            onConfirm: () => {
              const msg = encodeURIComponent(`Hello, my account with email ${email} appears to be suspended. Please assist.`);
              window.open(`https://wa.me/94771945267?text=${msg}`, '_blank', 'noopener');
            }
          });

          setLoading(false);
          return;
        }

        showError(errorMsg);
        setLoading(false);
        return;
      }

      // Success - save remember me preference
      if (rememberMe) {
        localStorage.setItem('rememberedEmail', email);
      } else {
        localStorage.removeItem('rememberedEmail');
      }

      // Do not redirect here; let useEffect handle it
      setLoading(false);
    } catch (error) {
      showError('An unexpected error occurred');
      setLoading(false);
    }
  };

  return (
    <div
      className="login min-h-screen flex items-center justify-center p-6 relative overflow-hidden"
      onMouseMove={(e) => {
        const root = e.currentTarget;
        const x = (e.clientX / window.innerWidth - 0.5) * 20; // -10 to 10
        const y = (e.clientY / window.innerHeight - 0.5) * 20;
        root.style.setProperty('--mx', `${x}px`);
        root.style.setProperty('--my', `${y}px`);
      }}
    >
      {/* Gradient overlay (sits above background image) */}
      <div className="absolute inset-0 -z-20 bg-gradient-to-br from-indigo-700 via-purple-700 to-pink-600 opacity-60 bg-gradient-overlay" aria-hidden></div>
      {/* Background (art + gradient overlay) */}
      {/* Background image: local copy at /public/images/login-bg.jpg is used first, Unsplash used as fallback */}
      <div className={`absolute inset-0 -z-30 login-bg-image ${bgLoaded ? 'bg-loaded' : ''}`} style={{ backgroundImage: "url('/images/login-bg.png'), url('https://images.unsplash.com/photo-1501785888041-af3ef285b470?auto=format&fit=crop&w=1650&q=80')", backgroundSize: 'cover', backgroundPosition: 'center', filter: 'saturate(1.05) contrast(1.02)' }} aria-hidden></div>

      {/* Decorative blobs */}
      <div className="absolute inset-0 -z-20 pointer-events-none" aria-hidden>
        <div className="absolute -left-40 -top-40 w-[26rem] h-[26rem] bg-purple-500 rounded-full opacity-25 blur-3xl animate-blob"></div>
        <div className="absolute -right-40 -bottom-40 w-[26rem] h-[26rem] bg-indigo-500 rounded-full opacity-25 blur-3xl animate-blob animation-delay-2000"></div>
      </div>

      {/* Subtle particle icons layer (positions controlled by CSS in index.css) */}
      <div className="absolute inset-0 -z-10 pointer-events-none" ref={particlesRef} aria-hidden>
        <div className="absolute inset-0">
          <div className="particle particle-0" />
          <div className="particle particle-1" />
          <div className="particle particle-2 flex items-center justify-center"><BookOpen className="w-3/4 h-3/4 text-white opacity-90" /></div>
          <div className="particle particle-3" />
          <div className="particle particle-4" />
          <div className="particle particle-5" />
          <div className="particle particle-6 flex items-center justify-center"><Check className="w-3/4 h-3/4 text-white opacity-90" /></div>
          <div className="particle particle-7" />
          <div className="particle particle-8" />
          <div className="particle particle-9" />
          <div className="particle particle-10" />
          <div className="particle particle-11 flex items-center justify-center"><Star className="w-3/4 h-3/4 text-white opacity-90" /></div>
          <div className="particle particle-12" />
          <div className="particle particle-13" />
          <div className="particle particle-14" />
          <div className="particle particle-15 flex items-center justify-center"><Check className="w-3/4 h-3/4 text-white opacity-90" /></div>
          <div className="particle particle-16" />
          <div className="particle particle-17" />
        </div>
      </div>

      <div className="relative w-full max-w-6xl flex items-center justify-center">
        {/* Right side - login form (centered) */}
        <div className="w-full max-w-md mx-auto">
          <div className="login__form bg-white/10 backdrop-blur-md rounded-3xl shadow-2xl p-8 border border-white/20 text-gray-100">
            {/* Header */}
            <div className="flex items-center gap-3 mb-6">
              <div className="inline-flex items-center justify-center w-14 h-14 bg-gradient-to-br from-indigo-600 to-purple-600 rounded-xl shadow-lg">
                <BookOpen className="w-7 h-7 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white">Welcome back</h1>
                <p className="text-sm text-white/75">Sign in to continue to LIT Academic</p>
              </div>
            </div>

            {errorMessage && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3 text-sm">
                <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
                <div className="flex-1 text-red-600">{errorMessage}</div>
                <button type="button" onClick={closeError} className="text-red-400 hover:text-red-600"><X className="w-4 h-4" /></button>
              </div>
            )}

            <form onSubmit={handleLogin} className="space-y-4">
              <div className="login__box">
                <label htmlFor="login-email" className="login__label-static">Email</label>
                <div className="login__input-wrap">
                  <div className="login__icon"><Mail className="text-white/90 w-5 h-5" /></div>
                  <input
                    id="login-email"
                    name="email"
                    autoComplete="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="student@gmail.com"
                    className="login__input-field"
                    required
                  />
                </div>
              </div> 

              <div className="login__box">
                <label htmlFor="login-password" className="login__label-static">Password</label>
                <div className="login__input-wrap">
                  <div className="login__icon"><Lock className="text-white/90 w-5 h-5" /></div>
                  <input
                    id="login-password"
                    name="password"
                    autoComplete="current-password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Password"
                    className="login__input-field"
                    required
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="login__eye" aria-label="Toggle password visibility">
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div> 

              <div className="login__check">
                <label className="login__check-group">
                  <input type="checkbox" checked={rememberMe} onChange={(e) => setRememberMe(e.target.checked)} className="login__check-input" />
                  <span className="login__check-label text-white/80">Remember me</span>
                </label>
                <button type="button" className="login__forgot text-sm text-white/80 hover:underline" onClick={() => {
                  const msg = encodeURIComponent(`Hello, I forgot my password for LIT Academic. My email is ${email || '[please enter email]'} — please help.`);
                  showConfirmation({
                    title: 'Forgot password',
                    message: 'You will be redirected to WhatsApp to request password assistance. Proceed?',
                    confirmText: 'Open WhatsApp',
                    cancelText: 'Cancel',
                    type: 'info',
                    onConfirm: () => window.open(`https://wa.me/94771945267?text=${msg}`, '_blank', 'noopener')
                  });
                }} aria-label="Forgot password via WhatsApp">Forgot password?</button>
              </div>

              <button type="submit" disabled={loading} className="login__button w-full relative overflow-hidden py-3 px-4 font-semibold hover:scale-[1.02] transition-all disabled:opacity-60">
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin w-5 h-5 text-white" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Signing in...
                  </span>
                ) : (
                  <span className="relative z-10">Sign In</span>
                )}
              </button>
            </form>

            <div className="mt-6 text-center text-white/80 text-sm">Don’t have an account? <button type="button" onClick={() => {
              showConfirmation({
                title: 'Open WhatsApp',
                message: 'You will be redirected to WhatsApp to request access. Proceed?',
                confirmText: 'Open WhatsApp',
                cancelText: 'Cancel',
                type: 'info',
                onConfirm: () => {
                  // Open WhatsApp in a new tab
                  window.open('https://wa.me/94771945267?text=Hello%2C%20I%20would%20like%20to%20request%20access%20to%20LIT%20Academic', '_blank', 'noopener');
                }
              });
            }} className="text-indigo-200 font-semibold hover:underline" aria-label="Request access via WhatsApp">Request access</button></div>
          </div>

          <p className="text-center text-white/70 text-sm mt-6">© 2026 LIT Academic. All rights reserved.</p>
        </div>
      </div>

      {/* Inline styles for particles and subtle animations */}
      <style>{`
        .animate-blob { animation: blob 8s infinite; }
        @keyframes blob {
          0% { transform: translate3d(0,0,0) scale(1); }
          33% { transform: translate3d(10px, -20px, 0) scale(1.05); }
          66% { transform: translate3d(-20px, 10px, 0) scale(0.95); }
          100% { transform: translate3d(0,0,0) scale(1); }
        }
        .particle { position: absolute; border-radius: 9999px; opacity: 0.9; mix-blend-mode: screen; }
        /* explicit particle positions & animation timings */
        ${particleCSS}
      `}</style>

    </div>
  );
};

export default LoginPage;
